<script setup>
import { useI18n } from 'vue-i18n';
import { POOLA_SUPPLIER_ALL_KEY } from '@/constants/poola.js';

defineProps({
  suppliers: {
    type: Array,
    required: true,
  },
  activeSupplier: {
    type: String,
    default: POOLA_SUPPLIER_ALL_KEY,
  },
});

const emit = defineEmits(['setSupplier']);
const { t } = useI18n();

function onChip(key) {
  emit('setSupplier', key);
}
</script>

<template>
  <div class="supplier-filter" role="toolbar">
    <div
      v-for="supplier in suppliers"
      :key="supplier.key"
      class="supplier-chip"
      :class="{ 'supplier-chip--active': activeSupplier === supplier.key }"
      role="button"
      tabindex="0"
      @click="onChip(supplier.key)"
      @keydown.enter="onChip(supplier.key)"
    >
      <div v-if="supplier.avatarUrl" class="supplier-chip__avatar-wrap">
        <img
          :src="supplier.avatarUrl"
          :alt="supplier.name || ''"
          class="supplier-chip__avatar"
          loading="lazy"
          @error="(e) => (e.target.style.display = 'none')"
        />
      </div>
      <span class="supplier-chip__name">{{ supplier.name ?? t('poola.buy.filter.all') }}</span>
      <f7-icon
        v-if="supplier.isPlatform && supplier.name"
        ios="f7:checkmark_seal_fill"
        md="material:verified"
        size="12"
        color="blue"
        class="supplier-chip__vetted"
      ></f7-icon>
    </div>
  </div>
</template>

<style scoped>
.supplier-filter {
  display: flex;
  gap: var(--poolit-spacing-sm);
  padding: var(--poolit-spacing-sm) var(--poolit-spacing-md);
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.supplier-filter::-webkit-scrollbar {
  display: none;
}

.supplier-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: var(--poolit-radius-full);
  font-size: 13px;
  font-weight: var(--poolit-font-weight-medium);
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  flex-shrink: 0;
  border: 1.5px solid rgba(0, 0, 0, 0.15);
  background: transparent;
  color: var(--f7-text-color, #111827);
  transition:
    background var(--poolit-transition-fast),
    border-color var(--poolit-transition-fast);
}

.supplier-chip--active {
  background: var(--poolit-primary-600, #7c3aed);
  border-color: var(--poolit-primary-600, #7c3aed);
  color: #fff;
}

.supplier-chip:active {
  opacity: 0.75;
}

.supplier-chip__avatar-wrap {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.supplier-chip__avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.supplier-chip__name {
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}

.supplier-chip__vetted {
  flex-shrink: 0;
}
</style>
